import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import _ from 'lodash';
import hash from 'object-hash';

import throttle from '../../util/throttle';
import { Loading } from '../Loading/Loading';
import { getInsightsSync, toggleCard, loadCardsHistory, cleanUpInsights } from './InsightsActions';
import InsightCard from './InsightCard';
import InsightCardSummary from './InsightCardSummary';

import './Insights.scss';

const TRIAL_ENDED_DAYS = 1;
const TRIAL_SOON_DAYS = 3;

export class Insights extends Component {
  static defaultProps = {};

  constructor(props) {
    super(props);

    this.state = {
      dataIsReady: false,
      data: {},
      dataHash: hash.MD5({}),
      abortController: null
    };

    this.getInsights = throttle(this.props.getInsights, 1000, this);
  }

  getFilters() {
    const { user, filters } = this.props;
    const insightsFollowFilters = user?.config?.insightsFollowFilters;
    const chart = filters?.chart;

    if (!insightsFollowFilters || !filters || !chart) {
      return null;
    }

    const chartFilters = filters[chart] || {};

    const extractId = (item) => item?.value?._id || item?._id || item?.value || item;

    const customFields = _(filters.allCustomFields || [])
      .map(cf => {
        const selectedValues = chartFilters[`selected${cf}`];
        if (selectedValues && selectedValues.length > 0) {
          return {
            fieldId: cf,
            values: _.map(selectedValues, extractId)
          };
        }
        return null;
      })
      .filter(cf => !!cf)
      .value();

    return {
      lists: _.map(chartFilters.selectedLists, extractId),
      labels: _.map(chartFilters.selectedLabels, extractId),
      members: _.map(chartFilters.selectedMembers, extractId),
      customFields
    };
  }

  async loadInsights(isRetry = false) {
    if (this.state.abortController) {
      this.state.abortController();
    }

    if (isRetry) {
      await new Promise(resolve => setTimeout(resolve, 10000));
    }

    const filters = this.getFilters();
    const abortFn = this.getInsights(this.props.dashboard._id, this.state.dataHash, this.state.startDate, this.state.endDate, filters);

    if (abortFn && typeof abortFn.then === 'function') {
      abortFn.then(abort => {
        if (abort && typeof abort === 'function') {
          this.setState({ abortController: abort });
        }
      });
    }
  }

  computeDataHash() {
    const { dashboard, dates, user } = this.props;
    const hashObj = {
      dashboardId: dashboard._id,
      startDate: dates.startDate,
      endDate: dates.endDate,
      useWorkingHoursInCycleTimeDays: user.config && user.config.useWorkingHoursInCycleTimeDays
    };

    if (user?.config?.insightsFollowFilters) {
      const filters = this.getFilters();
      if (filters) {
        hashObj.filters = filters;
      }
    }

    return hash.MD5(hashObj);
  }

  componentDidMount() {
    this.props.loadCardsHistory();
    if (!this.props.dataIsLoading && this.props.dataHash && this.props.dashboard && this.props.dates) {
      this.setState({
        startDate: this.props.dates.startDate,
        endDate: this.props.dates.endDate,
        dataHash: this.computeDataHash()
      });
    }
  }

  componentWillUnmount() {
    if (this.state.abortController) {
      this.state.abortController();
    }
    this.props.cleanUpInsights();
  }

  componentDidUpdate(prevProps, prevState) {
    const { user, filters } = this.props;
    const chart = filters?.chart;

    const filtersChanged = user?.config?.insightsFollowFilters && chart &&
      filters?.[chart] && prevProps.filters?.[chart] &&
      !_.isEqual(filters[chart], prevProps.filters[chart]);

    if (
      this.props.dashboard &&
      !this.props.dataIsLoading &&
      this.props.dataHash &&
      this.props.dates &&
      (
        prevProps.dataHash !== this.props.dataHash ||
        prevProps.dataIsLoading !== this.props.dataIsLoading ||
        prevProps.dashboard._id !== this.props.dashboard._id ||
        !_.isEqual(prevProps.dates, this.props.dates) ||
        filtersChanged
      )
    ) {
      this.setState({
        startDate: this.props.dates.startDate,
        endDate: this.props.dates.endDate,
        dataHash: this.computeDataHash()
      });
    }

    if (
      user && user.config.aiInsightsStatus !== 'expired' && (
        prevState.dataHash !== this.state.dataHash ||
        (prevProps.insightsError !== this.props.insightsError && this.props.insightsRetry && this.props.insightsError)
      )
    ) {
      this.loadInsights(this.props.insightsRetry);
    }
  }

  toggleCard = (id) => {
    this.props.toggleCard(id);
  }

  getTrialDays() {
    const { user } = this.props;
    return user.config.aiInsightsTrialEnd ? moment(user.config.aiInsightsTrialEnd).diff(moment(), 'days') + 1 : 90;
  }

  getTrialNote() {
    const { user } = this.props;
   
    if (user && user.config && user.config.aiInsightsStatus === 'trial') {
      const trialDays = this.getTrialDays();
      return `Your AI Insights trial expires in ${trialDays} day${trialDays !== 1 ? 's' : ''}`;
    }
  }

  getTrialTone() {
    const trialDays = this.getTrialDays();
    if (trialDays <= TRIAL_ENDED_DAYS) {
      return 'ended';
    }
    return trialDays <= TRIAL_SOON_DAYS ? 'soon' : '';
  }

  render() {
    const { insights, insightsLoading, insightsLoaded, insightsStreaming, insightsProgress, insightsError, insightsCards, user } = this.props;
    const customQuote = user.allowedDashboards > 10;
    const aiInsightsTrialNote = this.getTrialNote();
    const trialTone = aiInsightsTrialNote ? this.getTrialTone() : '';
    const isTrialExpired = user && user.config && user.config.aiInsightsStatus === 'expired';

    if (isTrialExpired) {
      return (
        <div className="insights-banner">
          <div className="insights-banner-message">Your AI Insights trial has expired.</div>
          {customQuote ? (
            <a className="btn btn-red" href="mailto:hello@getnave.com">Request pricing quote</a>
          ) : (
            <Link className="btn btn-red" to="/upgrade?from=insights">Upgrade</Link>
          )}
        </div>
      )
    }

    // Show loading state with progress message during streaming
    if (insightsLoading || !insightsLoaded) {
      return (
        <div className="insights-loading-container">
          <Loading progress={insightsError || insightsProgress} />
        </div>
      );
    }

    const hasInsights = insights && insights.insights && insights.insights.length;

    if (!hasInsights) {
      const insightsStatus = insights && (insights.noCache || insights.capExceeded) ?
        `No insights available.` :
        `No insights available. Please ensure data is loaded`;
      
      return (
        <div className="insights-empty">
          <p>{insightsStatus}</p>
        </div>
      );
    }

    const showSummary = false;

    const dateRangeLabel = this.state.startDate && this.state.endDate
      ? `Based on ${moment(this.state.startDate).format('DD MMM YYYY')} - ${moment(this.state.endDate).format('DD MMM YYYY')}`
      : null;

    return (
      <div className="insight-cards-wrap">
        {aiInsightsTrialNote && (
            <div className={`insight-trial-expires${trialTone ? ` ${trialTone}` : ''}`}>
              <span>{aiInsightsTrialNote}</span>
              {customQuote ? (
                <a href="mailto:hello@getnave.com">Request quote</a>
              ) : (
                <Link to="/upgrade?from=insights">Upgrade</Link>
              )}
            </div>
          )}
        {dateRangeLabel && (
          <div className="insight-data">
            <span>{dateRangeLabel}</span>
          </div>
        )}
        <div className="insight-cards-grid">
          {insights.summary && insights.modal && showSummary && (
            <InsightCardSummary cardSummary={insights.summary} modalData={insights.modal}/>
          )}
          {insights.insights.map((card) => (
            <InsightCard key={card.id} card={card} expanded={insightsCards?.[card.id]} onToggle={() => this.toggleCard(card.id)}/>
          ))}
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    getInsights: (dashboardId, dataHash, from, to, filters) => dispatch(getInsightsSync(dashboardId, dataHash, from, to, filters)),
    cleanUpInsights: () => dispatch(cleanUpInsights()),
    loadCardsHistory: () => dispatch(loadCardsHistory()),
    toggleCard: (id) => dispatch(toggleCard(id)),
  };
};

const mapStateToProps = state => {
  return {
    app: state.app.app,
    user: state.auth.user,
    dataIsLoading: state.app.isLoading,
    dataHash: state.app.dataHash,
    dashboard: state.dashboards.selected,
    filters: state.filters,
    dates: state.filters.dates,
    // insights
    insights: state.insights.insights,
    insightsError: state.insights.error,
    insightsRetry: state.insights.retry,
    insightsCards: state.insights.cards,
    insightsLoaded: state.insights.loaded,
    insightsLoading: state.insights.isLoading,
    insightsStreaming: state.insights.isStreaming,
    insightsProgress: state.insights.progress,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Insights);
